/** Caravans: creatures that walk to a town without a hero (H4, H5).
 *
 * The busywork this removes is the hero taxi. In the classic a creature bought at an
 * external dwelling reaches the front exactly one way - a hero walks out, picks
 * it up and walks back - which costs a week of somebody's movement and is the
 * least interesting week in the game. It is also why a far-flung dwelling is
 * usually not worth capturing: the stock accumulates and nobody ever goes to
 * get it. A caravan is the same creatures making the same journey with nobody
 * driving them.
 *
 * Three prices keep it from being free reinforcement, and they are the whole
 * balance argument:
 *
 *  1. **It is paid for when it sets out**, in full, at the source's own price.
 *     Nothing is bought on arrival and nothing is refunded if it does not.
 *  2. **It walks.** `CARAVAN_TILES_PER_DAY` is deliberately below a mounted
 *     hero's day, so distance is still what the map charges - a dwelling on the
 *     far side of the map is a week of waiting, not an instant delivery.
 *  3. **It goes to one named town, and can be lost.** If that town is not still
 *     its owner's on the day it arrives, the column is gone. So a caravan sent
 *     into a contested corner is a bet, which is what stops "buy everywhere,
 *     send everything home" from being the only opening anyone plays.
 *
 * Where this file sits in the adventure layering (see AGENTS.md): it imports
 * `town.ts` and `payload.ts` and is imported by `execute.ts`, `legal.ts` and
 * `turn.ts`. Nothing below it imports this, so the chain stays a chain.
 */

import type { CaravanId, CreatureDefId, ObjectId, PlayerId, TownId } from "../ids.js";
import { asCaravanId } from "../ids.js";
import type { Reason } from "../commands.js";
import { Err, Ok, type Result } from "../result.js";
import type { CaravanState, MapObjectState, Resources, TownState } from "../state.js";
import { RESOURCES } from "../state.js";
import { entityKeys } from "../util/iter.js";
import { type Ctx, type Mut, absoluteDay, addResource, mintId } from "./ctx.js";
import { availableOf, recruitCost, spendAvailable } from "./town.js";
import { addCreatures, hasFreeSlotFor } from "./hero.js";
import { payloadOf, payNum, payStr } from "./payload.js";
import { setPayload } from "./payload.js";
import { asCreatureDefId } from "./ids.js";

/**
 * Map cells a column covers in a day.
 *
 * Below a hero's day on purpose. A hero with Logistics crosses far more ground
 * than this, so sending the creatures is slower than fetching them - which is
 * the trade the whole feature turns on: the caravan costs time and the taxi
 * costs a hero. A number that matched a hero's pace would make the hero's trip
 * pointless rather than optional.
 */
export const CARAVAN_TILES_PER_DAY = 10;

/**
 * Extra days for a column that has to change level.
 *
 * A caravan does not path - it is not on the map, it is a date and a
 * destination - so the underworld cannot be charged for by walking it. A flat
 * toll is the honest stand-in: crossing between the surface and the underworld
 * means finding a gate, and a gate is never on the way.
 */
export const CARAVAN_PLANE_DAYS = 3;

/** Days a column takes, from one cell to another. At least one. */
export function caravanDays(
  from: { readonly x: number; readonly y: number; readonly z: number },
  to: { readonly x: number; readonly y: number; readonly z: number },
): number {
  // Chebyshev, because the map's own steps are eight-way: a diagonal is one
  // step, so a diagonal run must not be priced as two.
  const dist = Math.max(Math.abs(to.x - from.x), Math.abs(to.y - from.y));
  const plane = to.z === from.z ? 0 : CARAVAN_PLANE_DAYS;
  const days = Math.ceil(dist / CARAVAN_TILES_PER_DAY) + plane;
  return days < 1 ? 1 : days;
}

// ------------------------------------------------------------------ sources

/** What one source can send, and what it costs. Null when it can send nothing. */
export interface CaravanSource {
  readonly creature: CreatureDefId;
  readonly stock: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** Every creature a town could send, with the pool it would come out of. */
export function townSources(c: Ctx, t: TownState): readonly CaravanSource[] {
  const out: CaravanSource[] = [];
  for (const key of Object.keys(t.available).sort()) {
    const def = asCreatureDefId(key);
    if (c.content.creatures.get(def) === undefined) continue;
    const stock = availableOf(c, t, def);
    if (stock > 0) out.push({ creature: def, stock, x: t.x, y: t.y, z: t.z });
  }
  return out;
}

/** An external dwelling this player owns, or null. Ownership only - what is
 *  standing in it is `objectSource`'s question and has its own refusal. */
function ownedDwelling(c: Ctx, id: ObjectId, owner: PlayerId): MapObjectState | null {
  const o = c.s.objects[id];
  if (o === undefined || o.removed || o.kind !== "dwelling") return null;
  return o.owner === owner ? o : null;
}

/** What an owned external dwelling could send. Null when it is not one, is not
 *  owned by `owner`, or has nothing in it. */
export function objectSource(c: Ctx, id: ObjectId, owner: PlayerId): CaravanSource | null {
  const o = ownedDwelling(c, id, owner);
  if (o === null) return null;
  const pay = payloadOf(c, o);
  const creature = asCreatureDefId(payStr(pay, "creature"));
  if (c.content.creatures.get(creature) === undefined) return null;
  const stock = payNum(pay, "stock", 0);
  if (stock <= 0) return null;
  return { creature, stock, x: o.x, y: o.y, z: o.z };
}

// --------------------------------------------------------------- validation

export interface PreparedCaravan {
  readonly source: CaravanSource;
  readonly cost: Partial<Resources>;
  readonly days: number;
  readonly owner: PlayerId;
}

/**
 * Every reason a dispatch can be refused, in the order a player would ask them:
 * is the destination mine, is the source mine, has it got these creatures, will
 * they fit when they land, and can I pay for them.
 *
 * The "will they fit" question is asked here and asked AGAIN on arrival, and
 * both are needed: a garrison with room today can be full in four days, and a
 * column that arrives at a full hall waits at the gate rather than evaporating.
 * Refusing at dispatch is only so that an obviously doomed column is not sold.
 */
export function validateCaravan(
  c: Ctx, to: TownId, fromTown: TownId | null, fromObject: ObjectId | null,
  creature: CreatureDefId, count: number,
): Result<PreparedCaravan, Reason> {
  if ((fromTown === null) === (fromObject === null)) {
    return Err({ code: "TARGET_INVALID" });
  }
  if (!Number.isInteger(count) || count <= 0) return Err({ code: "TARGET_INVALID" });

  const dest = c.s.towns[to];
  if (dest === undefined) return Err({ code: "NO_SUCH_ENTITY", id: to as string });
  const owner = dest.owner;
  if (owner === null) return Err({ code: "NOT_OWNED", id: to as string });

  let source: CaravanSource | null = null;
  if (fromTown !== null) {
    const t = c.s.towns[fromTown];
    if (t === undefined) return Err({ code: "NO_SUCH_ENTITY", id: fromTown as string });
    if (t.owner !== owner) return Err({ code: "NOT_OWNED", id: fromTown as string });
    // A town cannot send to itself: the creatures are already there, and a
    // caravan that arrived where it started would be a way to launder a week's
    // growth past the "will they fit" check.
    if (fromTown === to) return Err({ code: "TARGET_INVALID" });
    const stock = availableOf(c, t, creature);
    if (stock <= 0) return Err({ code: "NONE_AVAILABLE", creature, have: 0, need: count });
    if (stock < count) return Err({ code: "NONE_AVAILABLE", creature, have: stock, need: count });
    source = { creature, stock, x: t.x, y: t.y, z: t.z };
  } else if (fromObject !== null) {
    const src = objectSource(c, fromObject, owner);
    if (src === null) {
      // Two different refusals, kept apart because they send a caller to two
      // different places: a dwelling somebody else holds is a map problem, and
      // an empty one you do hold is a "come back next week" problem. They used
      // to be one `NOT_OWNED`, which made an emptied dwelling report as stolen.
      const o = ownedDwelling(c, fromObject, owner);
      if (o === null) return Err({ code: "NOT_OWNED", id: fromObject as string });
      return Err({ code: "NONE_AVAILABLE", creature, have: 0, need: count });
    }
    if (src.creature !== creature) return Err({ code: "TARGET_INVALID" });
    if (src.stock < count) {
      return Err({ code: "NONE_AVAILABLE", creature, have: src.stock, need: count });
    }
    source = src;
  }
  if (source === null) return Err({ code: "TARGET_INVALID" });

  if (c.content.creatures.get(creature) === undefined) {
    return Err({ code: "NO_SUCH_ENTITY", id: creature as string });
  }
  const garrison = c.s.armies[dest.garrison];
  if (garrison === undefined) return Err({ code: "NO_SUCH_ENTITY", id: dest.garrison as string });
  if (!hasFreeSlotFor(garrison, creature)) return Err({ code: "ARMY_FULL" });

  const cost = recruitCost(c.content, creature, count);
  const pl = c.s.players[owner];
  if (pl === undefined) return Err({ code: "NO_SUCH_ENTITY", id: owner as string });
  for (const r of RESOURCES) {
    const need = cost[r] ?? 0;
    const have = pl.resources[r];
    if (need > have) return Err({ code: "INSUFFICIENT_RESOURCES", resource: r, have, need });
  }

  return Ok({ source, cost, days: caravanDays(source, dest), owner });
}

/** Pay for a column and put it on the road. */
export function sendCaravan(
  c: Ctx, to: TownId, fromTown: TownId | null, fromObject: ObjectId | null,
  creature: CreatureDefId, count: number,
): Result<CaravanId, Reason> {
  const r = validateCaravan(c, to, fromTown, fromObject, creature, count);
  if (!r.ok) return r;
  const { source, cost, days, owner } = r.value;

  for (const res of RESOURCES) {
    const v = cost[res] ?? 0;
    if (v > 0) addResource(c, owner, res, -v);
  }
  if (fromTown !== null) spendAvailable(c, fromTown, creature, count);
  else if (fromObject !== null) {
    const o = c.s.objects[fromObject]!;
    setPayload(c, o.id, { stock: payNum(payloadOf(c, o), "stock", 0) - count });
  }

  const id = asCaravanId(mintId(c, "caravan"));
  const caravan: CaravanState = {
    id, owner, x: source.x, y: source.y, z: source.z,
    to, def: creature, count,
    // Absolute, as `CaravanState` says: `turn.day` is the day of the WEEK, and
    // a column keyed on it never arrived once its trip crossed a Monday.
    arrivesOnDay: absoluteDay(c.s) + days,
  };
  (c.s.caravans as Mut<Record<string, CaravanState>>)[id as string] = caravan;
  c.events.push({
    type: "CaravanSent", caravan: id, town: to, creature, count, arrivesOnDay: caravan.arrivesOnDay,
  });
  return Ok(id);
}

// ---------------------------------------------------------------- arrival

/**
 * Walk every column that is due and deliver what it can.
 *
 * Called once from the day tick, after ownership for the new day is settled, so
 * a town taken this morning does not receive the reinforcements it was expecting
 * this afternoon.
 *
 * Three outcomes and no fourth: it lands, it waits at the gate because the
 * garrison is full, or it is lost because the town is not its owner's any more.
 * A column is never re-routed - a caravan that hunted for a friendly town would
 * make sending one riskless, and the risk is what prices it.
 */
export function deliverCaravans(c: Ctx): void {
  for (const id of entityKeys(c.s.caravans)) {
    const cv = c.s.caravans[id];
    if (cv === undefined) continue;
    if (absoluteDay(c.s) < cv.arrivesOnDay) continue;

    const t = c.s.towns[cv.to];
    if (t === undefined || t.owner !== cv.owner) {
      removeCaravan(c, cv.id);
      c.events.push({ type: "CaravanLost", caravan: cv.id, town: cv.to, player: cv.owner });
      continue;
    }
    // The garrison, not whichever hero happens to be standing in the hall: a
    // column arrives at a place, and the hall is the place. A hero who wants
    // them picks them up the way he picks up any garrison.
    const garrison = c.s.armies[t.garrison];
    if (garrison === undefined || !hasFreeSlotFor(garrison, cv.def)) continue;
    addCreatures(c, t.garrison, cv.def, cv.count);
    removeCaravan(c, cv.id);
    c.events.push({
      type: "CaravanArrived", caravan: cv.id, town: cv.to, creature: cv.def, count: cv.count,
    });
  }
}

/** Every column a player has on the road, oldest id first. */
export function caravansOf(c: Ctx, owner: PlayerId): readonly CaravanState[] {
  const out: CaravanState[] = [];
  for (const id of entityKeys(c.s.caravans)) {
    const cv = c.s.caravans[id];
    if (cv !== undefined && cv.owner === owner) out.push(cv);
  }
  return out;
}

function removeCaravan(c: Ctx, id: CaravanId): void {
  const next: Record<string, CaravanState> = {};
  for (const k of entityKeys(c.s.caravans)) {
    if (k === (id as string)) continue;
    const cv = c.s.caravans[k];
    if (cv !== undefined) next[k] = cv;
  }
  (c.s as Mut<{ caravans: Record<string, CaravanState> }>).caravans = next;
}
